<template>
  <PopoverRoot v-model:open="isOpen">
    <PopoverTrigger
      :id="fieldContext?.inputId"
      ref="trigger"
      :disabled="isDisabled"
      :class="[
        `design-system-input flex w-full items-center rounded-lg border text-left text-sm transition`,
        `outline-2 -outline-offset-1 focus-visible:outline`,
        isDisabled && `opacity-50`,
        fieldContext?.validationStatus.value === 'error'
          ? `border-red-300 outline-red-500 dark:border-red-800`
          : `outline-blue-500`,
      ]"
      :aria-invalid="fieldContext?.validationStatus.value === 'error'"
      :aria-describedby="ariaDescribedBy"
      @focus="onFocus"
      @blur="onBlur"
    >
      <div class="flex min-w-0 grow items-center gap-2.5 px-2.5 py-1.5">
        <div class="flex shrink-0 items-center">
          <div
            class="size-4 rounded-full border border-border"
            :style="{ backgroundColor: activeColor || undefined }"
          />
          <div
            v-if="isDual"
            class="-ml-1.5 size-4 rounded-full border border-border"
            :style="{ backgroundColor: darkModelValue || undefined }"
          />
        </div>

        <span v-if="activeColor" class="min-w-0 grow truncate">
          {{ activeLabel }}
        </span>
        <span v-else class="min-w-0 grow truncate text-muted-foreground">
          Select a color
        </span>
      </div>

      <div class="grid size-8 shrink-0 place-items-center">
        <ChevronDownIcon class="size-4 text-muted-foreground" />
      </div>
    </PopoverTrigger>

    <PopoverPortal>
      <Transition
        enter-active-class="transition-opacity duration-100 ease-out"
        enter-from-class="opacity-0"
        enter-to-class="opacity-100"
        leave-active-class="transition duration-75 ease-in"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <PopoverContent
          align="start"
          :side-offset="8"
          :collision-padding="10"
          class="z-popover rounded-lg border border-border bg-background shadow-xl"
        >
          <div v-if="isDual" class="flex gap-1 border-b border-border p-1.5">
            <button
              type="button"
              :class="[
                `flex grow items-center justify-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition`,
                activeTab === 'light'
                  ? `bg-card text-foreground shadow-sm`
                  : `text-muted-foreground hover:text-foreground`,
              ]"
              @click="activeTab = 'light'"
            >
              <SunIcon class="size-3" />
              Light
            </button>
            <button
              type="button"
              :class="[
                `flex grow items-center justify-center gap-1.5 rounded-md px-2.5 py-1 text-xs font-medium transition`,
                activeTab === 'dark'
                  ? `bg-card text-foreground shadow-sm`
                  : `text-muted-foreground hover:text-foreground`,
              ]"
              @click="activeTab = 'dark'"
            >
              <MoonIcon class="size-3" />
              Dark
            </button>
          </div>

          <ColorPicker
            v-if="activeTab === 'light'"
            v-model="modelValue"
            :presets="props.presets"
          />
          <ColorPicker
            v-else
            v-model="darkModelValue"
            :presets="props.darkPresets ?? props.presets"
          />
        </PopoverContent>
      </Transition>
    </PopoverPortal>
  </PopoverRoot>
</template>

<script setup lang="ts">
import {
  PopoverContent,
  PopoverPortal,
  PopoverRoot,
  PopoverTrigger,
} from "reka-ui";
import { computed, onMounted, ref, useTemplateRef } from "vue";

import ChevronDownIcon from "@/icons/heroicons/chevron-down-micro.svg";
import MoonIcon from "@/icons/heroicons/moon-micro.svg";
import SunIcon from "@/icons/heroicons/sun-micro.svg";

import { injectFieldContext } from "./context";
import ColorPicker from "./internal/ColorPicker.vue";
import { type ColorPreset, COLOR_PRESETS } from "./internal/colors";

const modelValue = defineModel<string>({ default: "" });
const darkModelValue = defineModel<string>("darkColor", { default: "" });

const props = defineProps<{
  disabled?: boolean;
  autofocus?: boolean;
  presets?: ColorPreset[];
  dual?: boolean;
  darkPresets?: ColorPreset[];
  onFocus?: (event: FocusEvent) => void;
  onBlur?: (event: FocusEvent) => void;
}>();

const isDual = computed(() => props.dual);

const activeTab = ref<"light" | "dark">("light");

const activeColor = computed(() => {
  return activeTab.value === "light" ? modelValue.value : darkModelValue.value;
});

const activeLabel = computed(() => {
  const color = activeColor.value;
  const presets =
    activeTab.value === "light"
      ? props.presets ?? COLOR_PRESETS
      : props.darkPresets ?? props.presets ?? COLOR_PRESETS;
  const preset = presets.find((p) => p.color === color);

  return preset ? preset.label : color.toLowerCase();
});

const fieldContext = injectFieldContext(null);

const isDisabled = computed(() => {
  return fieldContext?.disabled.value || props.disabled;
});

const ariaDescribedBy = computed(() => {
  if (!fieldContext) {
    return undefined;
  }

  return [
    fieldContext.descriptionId,
    fieldContext.hintId,
    fieldContext.validationMessageId,
  ].join(" ");
});

const isOpen = ref(false);
const trigger = useTemplateRef("trigger");

onMounted(() => {
  if (props.autofocus) {
    trigger.value?.$el?.focus({ preventScroll: true });
  }
});

defineExpose({
  input: computed(() => trigger.value?.$el as HTMLButtonElement | null),
});
</script>
